// ── COPIA dalla squadra Pit ─────────────────────────────────────────────────
// Preso tale e quale da `gleem-ops:/home/andre/wt-pit-sickbay-fe/frontend/src/core/monitor/sickbayRedact.ts`
// (branch feat/sickbay-frontend-dsn del Pit, 06/09/2026), che a sua volta e' il port riga per riga di
// `sickbay/clients/sickbay_redact.py` (jaz-core). Il canonico resta il Python: si corregge LA' e si ricopia.

/**
 * Redazione degli indirizzi email dagli eventi diretti a Sickbay (Sentry/GlitchTip).
 *
 * Replica in TypeScript, riga per riga, di `sickbay/clients/sickbay_redact.py`
 * (commit 6689732). Il canonico e' quello: se va corretto, si corregge LA' e si
 * riporta qui. I nomi sono gli stessi apposta, per ritrovarsi.
 *
 * Perche' esiste (06/09/2026). Le app della flotta redigono la PII **per nome di
 * chiave**: un dizionario con la chiave `email` diventa `[redacted]`. Non regge quando
 * l'indirizzo e' *dentro un valore*: il messaggio di una `UniqueViolation` riporta la
 * riga che ha sbattuto contro il vincolo, indirizzo compreso, e nessuna chiave si chiama
 * `email`. Cosi' 25 indirizzi di clienti veri sono finiti nel motore in un solo evento.
 *
 * Questo modulo lavora sui VALORI, non sui nomi: cammina tutto l'evento (messaggio,
 * valore delle eccezioni, breadcrumb, extra, contexts, tags, request, user, chiavi degli
 * oggetti) e sostituisce ogni indirizzo con un marcatore.
 *
 * La sostituzione: `mario.rossi@calzedonia.it` -> `<email:calzedonia.it>`
 * Butta la parte locale (quella che identifica la persona) e tiene il dominio (quello
 * che rende l'errore ancora diagnosticabile: quale tenant, quale controparte). Non un
 * hash: il marcatore deve essere stabile, cosi' N clienti diversi collassano in un solo
 * gruppo del motore invece di una issue per cliente.
 *
 * Fallisce CHIUSO: se la redazione solleva, l'evento viene SCARTATO (`null`), non
 * spedito com'e'. Meglio perdere un evento che una persona.
 *
 * Uso con @sentry/vue:
 *   Sentry.init({ ..., beforeSend: redigiEvento })
 * Per comporlo con un altro redattore: `componi(redigiEvento, altro)`.
 */

/**
 * Parte locale: il set comune (niente `=`, `?`, `&`, `/`, che in testo libero
 * farebbero inghiottire il `?email=` che precede l'indirizzo). Dominio: almeno
 * un'etichetta + TLD.
 *
 * La chiocciola vale anche url-encoded: nei breadcrumb un indirizzo arriva dentro una
 * URL come `?q=direzione%40gruppoeconova.eu` (o `%2540`, se ricodificata), e con la
 * sola `@` passava intatta.
 */
export const EMAIL_RE =
  /[A-Za-z0-9._%+'-]+(?:@|%40|%2540)((?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,})/gi

const PROFONDITA_MAX = 16

/** Un `beforeSend`: riceve l'evento, torna l'evento redatto o `null` per scartarlo. */
export type Redattore<E> = (event: E, hint?: unknown) => E | null

/**
 * Sostituisce ogni indirizzo in una stringa.
 *
 * L'uscita rapida controlla `@` **e** `%`: cercare la sola `%40` non basta, perche' in
 * una chiocciola doppiamente codificata (`%2540`) quella sottostringa non compare e la
 * scorciatoia scavalcava una regex che invece l'avrebbe presa.
 */
export function redigiTesto(testo: string): string {
  if (!testo.includes('@') && !testo.includes('%')) return testo
  return testo.replace(EMAIL_RE, '<email:$1>')
}

function eOggettoPiatto(nodo: object): nodo is Record<string, unknown> {
  const proto = Object.getPrototypeOf(nodo)
  return proto === Object.prototype || proto === null
}

function cammina(nodo: unknown, prof = 0): unknown {
  if (prof > PROFONDITA_MAX) return nodo
  if (typeof nodo === 'string') return redigiTesto(nodo)
  if (Array.isArray(nodo)) return nodo.map((v) => cammina(v, prof + 1))
  if (nodo !== null && typeof nodo === 'object' && eOggettoPiatto(nodo)) {
    const fuori: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(nodo)) {
      fuori[redigiTesto(k)] = cammina(v, prof + 1)
    }
    return fuori
  }
  return nodo
}

/**
 * `beforeSend` / `beforeSendTransaction`. Torna una COPIA redatta; scarta l'evento
 * (`null`) se non riesce a redigerlo.
 */
export function redigiEvento<E>(event: E, _hint?: unknown): E | null {
  if (event === null || event === undefined) return null
  try {
    return cammina(event) as E
  } catch {
    // fallire chiuso: meglio perdere l'evento che la persona
    return null
  }
}

/** Incatena piu' `beforeSend`; il primo che scarta ferma la catena. */
export function componi<E>(...funzioni: Redattore<E>[]): Redattore<E> {
  return (event, hint) => {
    let corrente: E | null = event
    for (const f of funzioni) {
      if (corrente === null) return null
      corrente = f(corrente, hint)
    }
    return corrente
  }
}
